import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Lock, User, Loader2, AlertCircle, Check } from 'lucide-react';
import { useAuthStore } from '../stores/auth.store';
import Logo from '../components/shared/Logo';

const registerSchema = z.object({
  email: z.string().email('Ungültige E-Mail-Adresse'),
  displayName: z.string().min(2, 'Name muss mindestens 2 Zeichen haben').max(100),
  password: z
    .string()
    .min(8, 'Mindestens 8 Zeichen')
    .regex(/[A-Z]/, 'Mindestens ein Großbuchstabe')
    .regex(/[0-9]/, 'Mindestens eine Zahl'),
  confirmPassword: z.string(),
  acceptTerms: z.boolean().refine((val) => val === true, {
    message: 'Du musst die Nutzungsbedingungen akzeptieren',
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwörter stimmen nicht überein',
  path: ['confirmPassword'],
});

type RegisterForm = z.infer<typeof registerSchema>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register: registerUser, isLoading, error, setError } = useAuthStore();
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      acceptTerms: false,
    },
  });
  
  const password = watch('password', '');
  
  const passwordRequirements = [
    { met: password.length >= 8, text: 'Mindestens 8 Zeichen' },
    { met: /[A-Z]/.test(password), text: 'Ein Großbuchstabe' },
    { met: /[0-9]/.test(password), text: 'Eine Zahl' },
  ];
  
  const onSubmit = async (data: RegisterForm) => {
    setError(null);
    const success = await registerUser(data.email, data.password, data.displayName);
    if (success) {
      navigate('/onboarding', { replace: true });
    }
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Logo className="h-10 mx-auto mb-6" />
          <h1 className="text-h1 text-text-primary">Account erstellen</h1>
          <p className="mt-2 text-text-muted">
            14 Tage kostenlos testen — keine Kreditkarte nötig
          </p>
        </div>
        
        <div className="bg-bg-surface border border-border-default rounded-xl p-6 backdrop-blur-xl">
          {error && (
            <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-center gap-2 text-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="text"
                  {...register('displayName')}
                  className="w-full pl-10 pr-4 py-2.5 bg-bg-primary border border-border-default rounded-lg text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="Dein Name"
                />
              </div>
              {errors.displayName && (
                <p className="mt-1 text-xs text-error">{errors.displayName.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                E-Mail
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="email"
                  {...register('email')}
                  className="w-full pl-10 pr-4 py-2.5 bg-bg-primary border border-border-default rounded-lg text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="deine@email.de"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-xs text-error">{errors.email.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Passwort
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="password"
                  {...register('password')}
                  className="w-full pl-10 pr-4 py-2.5 bg-bg-primary border border-border-default rounded-lg text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="••••••••"
                />
              </div>
              <div className="mt-2 space-y-1">
                {passwordRequirements.map((req, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <Check className={`w-3 h-3 ${req.met ? 'text-success' : 'text-text-disabled'}`} />
                    <span className={req.met ? 'text-success' : 'text-text-muted'}>
                      {req.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1.5">
                Passwort bestätigen
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                <input
                  type="password"
                  {...register('confirmPassword')}
                  className="w-full pl-10 pr-4 py-2.5 bg-bg-primary border border-border-default rounded-lg text-text-primary placeholder:text-text-disabled focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  placeholder="••••••••"
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-xs text-error">{errors.confirmPassword.message}</p>
              )}
            </div>
            
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="acceptTerms"
                {...register('acceptTerms')}
                className="mt-1 rounded border-border-default bg-bg-primary text-primary focus:ring-primary/40"
              />
              <label htmlFor="acceptTerms" className="text-sm text-text-muted">
                Ich akzeptiere die{' '}
                <Link to="/agb" className="text-primary hover:underline">
                  AGB
                </Link>{' '}
                und{' '}
                <Link to="/datenschutz" className="text-primary hover:underline">
                  Datenschutzerklärung
                </Link>
              </label>
            </div>
            {errors.acceptTerms && (
              <p className="text-xs text-error">{errors.acceptTerms.message}</p>
            )}
            
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-primary to-violet-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Registrieren...
                </>
              ) : (
                'Kostenlos registrieren'
              )}
            </button>
          </form>
          
          <div className="mt-6 text-center text-sm text-text-muted">
            Bereits registriert?{' '}
            <Link
              to="/login"
              className="text-primary hover:text-primary-hover transition-colors font-medium"
            >
              Jetzt anmelden
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
